// Generic drum pattern component - shows a rhythm grid based on time_signature.
// Default patterns per time signature. Users see:
// HH (hi-hat) on every 8th note, Snare on backbeats (2 & 4 for 4/4), Kick on 1 & 3.

const PATTERNS = {
  '4/4':  { beats: 4, subdivs: 2, kick: [0, 4], snare: [2, 6], hh: [0,1,2,3,4,5,6,7] },
  '3/4':  { beats: 3, subdivs: 2, kick: [0, 4], snare: [2],    hh: [0,1,2,3,4,5] },
  '2/4':  { beats: 2, subdivs: 2, kick: [0],    snare: [2],    hh: [0,1,2,3] },
  '6/8':  { beats: 6, subdivs: 1, kick: [0, 3], snare: [1, 4], hh: [0,1,2,3,4,5] },
  '12/8': { beats: 12, subdivs: 1, kick: [0, 3, 6, 9], snare: [3, 9], hh: [0,1,2,3,4,5,6,7,8,9,10,11] },
  '5/4':  { beats: 5, subdivs: 2, kick: [0, 6], snare: [2, 8], hh: [0,1,2,3,4,5,6,7,8,9] },
  '7/8':  { beats: 7, subdivs: 1, kick: [0, 4], snare: [2, 6], hh: [0,1,2,3,4,5,6] },
};

export default function DrumPattern({ timeSignature = '4/4', bpm }) {
  const pat = PATTERNS[timeSignature] || PATTERNS['4/4'];
  const cells = pat.beats * pat.subdivs;
  const cellW = 30;
  const rowH = 26;
  const trackLabelW = 60;
  const totalW = trackLabelW + cellW * cells;
  const totalH = rowH * 3 + 30;

  const tracks = [
    { label: 'HH',    hits: pat.hh,    color: '#78716c' },
    { label: 'Snare', hits: pat.snare, color: '#c2410c' },
    { label: 'Kick',  hits: pat.kick,  color: '#78350f' },
  ];

  return (
    <div className="card-stone p-4 overflow-x-auto" data-testid="drum-pattern">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-stone-400">Pattern batterie · {timeSignature}</div>
        {bpm && <div className="text-xs text-stone-500">{bpm} BPM</div>}
      </div>
      <svg viewBox={`0 0 ${totalW} ${totalH}`} width="100%" style={{ maxWidth: totalW }} className="block">
        {/* Beat headers */}
        {Array.from({ length: pat.beats }).map((_, b) => (
          <text
            key={b}
            x={trackLabelW + (b * pat.subdivs + pat.subdivs / 2) * cellW}
            y={14}
            textAnchor="middle"
            fontSize={12}
            fill="#a8a29e"
            fontWeight="600"
          >{b + 1}</text>
        ))}
        {/* Rows */}
        {tracks.map((t, ti) => {
          const y = 24 + ti * rowH;
          return (
            <g key={t.label}>
              {/* Track label */}
              <text x={0} y={y + rowH * 0.6} fontSize={11} fill="#e7e5e4" fontWeight="600">{t.label}</text>
              {/* Grid cells */}
              {Array.from({ length: cells }).map((_, c) => {
                const x = trackLabelW + c * cellW;
                const isHit = t.hits.includes(c);
                const isBeatStart = c % pat.subdivs === 0;
                return (
                  <g key={c}>
                    <rect
                      x={x}
                      y={y + 4}
                      width={cellW - 3}
                      height={rowH - 8}
                      fill={isHit ? t.color : '#1c1917'}
                      stroke={isBeatStart ? '#57534e' : '#292524'}
                      strokeWidth={isBeatStart ? 1.5 : 0.5}
                      rx={3}
                    />
                    {isHit && (
                      <circle
                        cx={x + (cellW - 3) / 2}
                        cy={y + rowH / 2}
                        r={4}
                        fill="#fafaf9"
                      />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="mt-3 text-xs text-stone-500 space-y-1">
        <div><span className="inline-block w-2 h-2 rounded-full bg-stone-500 mr-2"></span>HH = charleston · joué en 8èmes (2 par temps)</div>
        <div><span className="inline-block w-2 h-2 rounded-full bg-orange-700 mr-2"></span>Snare = caisse claire · sur les backbeats (temps 2 & 4)</div>
        <div><span className="inline-block w-2 h-2 rounded-full bg-amber-900 mr-2"></span>Kick = grosse caisse · sur les temps forts (1 & 3)</div>
      </div>
    </div>
  );
}
